import { getAllVehiclesOptions } from '@/api/main'
import ErrorState from '@/components/Common/ErrorState'
import PageShell from '@/components/Common/PageShell'
import { RouteLoading } from '@/components/Common/RouteLoader'
import { ConfigureView } from '@/components/ConfigureView'
import { DriveMapModal } from '@/components/DriveMap/DriveMapModal'
import Scheduler from '@/components/Scheduling/Scheduler'
import SchedulerPageShell from '@/components/Scheduling/SchedulerPageShell'
import { Channels } from '@/types/misc'
import { requirePermission } from '@/utils/auth'
import { ActionIcon, Button, Group, Tooltip } from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'
import { useDisclosure, useLocalStorage, useDebouncedCallback } from '@mantine/hooks'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router'
import { Suspense, useMemo, useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { getTimezone } from '@/utils/datetime'
import { getDefaultSchedulingBounds } from '@/utils/schedulingBounds'
import { TbChevronLeft, TbChevronRight, TbMap2 } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'

dayjs.extend(utc)
dayjs.extend(timezone)

const ERRORS = {
  START_DATE_AFTER_END_DATE: i18next.t('startDateAfterEndDate', { ns: 'scheduling' }),
  END_DATE_BEFORE_START_DATE: i18next.t('endDateBeforeStartDate', { ns: 'scheduling' }),
}

export const Route = createFileRoute('/scheduling/$startDate/$endDate')({
  beforeLoad: ({ params }) => {
    requirePermission('scheduling')

    // Validate date range from URL parameters
    const decodedStartDate = decodeURIComponent(params.startDate)
    const decodedEndDate = decodeURIComponent(params.endDate)

    const startDate = dayjs.utc(decodedStartDate)
    const endDate = dayjs.utc(decodedEndDate)

    if (!startDate.isValid() || !endDate.isValid()) {
      throw new Error(i18next.t('invalidDateFormat', { ns: 'scheduling' }))
    }

    if (startDate.isAfter(endDate)) {
      throw new Error(ERRORS.START_DATE_AFTER_END_DATE)
    }

    if (endDate.isBefore(startDate)) {
      throw new Error(ERRORS.END_DATE_BEFORE_START_DATE)
    }
  },
  component: () => (
    <Suspense fallback={<RouteLoading title={i18next.t('scheduling', { ns: 'scheduling' })} />}>
      <SchedulingMainPage />
    </Suspense>
  ),
  errorComponent: ({ error }) => {
    if (error.message === ERRORS.START_DATE_AFTER_END_DATE || error.message === ERRORS.END_DATE_BEFORE_START_DATE) {
      return (
        <PageShell title={i18next.t('errorLoadingSchedules', { ns: 'scheduling' })} fullWidth>
          <ErrorState
            title={i18next.t('invalidDateRange', { ns: 'scheduling' })}
            message={error.message}
            backButtonHref="/scheduling"
          />
        </PageShell>
      )
    }

    return (
      <PageShell title={i18next.t('errorLoadingSchedules', { ns: 'scheduling' })} fullWidth>
        <ErrorState title={i18next.t('oopsSomethingWentWrong', { ns: 'common' })} message={error.message} />
      </PageShell>
    )
  },
})

const CHANNELS_STORAGE_KEY = 'scheduling-channels'

function SchedulingMainPage() {
  const { t } = useTranslation('scheduling')
  const { startDate, endDate } = Route.useParams()
  const navigate = useNavigate()
  const location = useLocation()

  // Hide scheduler if we're on the error route (full page error)
  const isErrorRoute = location.pathname.includes('/error')

  const startDateObj = useMemo(() => {
    const decoded = decodeURIComponent(startDate)
    // Convert UTC date from URL to configured timezone for date picker display
    return dayjs.utc(decoded).tz(getTimezone()).toDate()
  }, [startDate])

  const endDateObj = useMemo(() => {
    const decoded = decodeURIComponent(endDate)
    // Convert UTC date from URL to configured timezone for date picker display
    return dayjs.utc(decoded).tz(getTimezone()).toDate()
  }, [endDate])

  const [localStartDate, setLocalStartDate] = useState<Date | null>(startDateObj)
  const [localEndDate, setLocalEndDate] = useState<Date | null>(endDateObj)
  const [mapOpened, { open: openMap, close: closeMap }] = useDisclosure(false)

  // Update local state when route params change
  useEffect(() => {
    setLocalStartDate(startDateObj)
    setLocalEndDate(endDateObj)
  }, [startDateObj, endDateObj])

  const handleStartDateClose = () => {
    if (!localStartDate) return

    const decoded = decodeURIComponent(startDate)
    // Compare with UTC date converted to configured timezone
    const currentStartDate = dayjs.utc(decoded).tz(getTimezone()).toDate()
    const startDateChanged = !dayjs(localStartDate).isSame(currentStartDate, 'minute')

    if (startDateChanged) {
      const endDateToCheck = localEndDate || endDateObj
      let adjustedStartDate = localStartDate
      let adjustedEndDate = endDateToCheck

      // If start date is after end date, use default scheduling bounds
      if (dayjs(localStartDate).isAfter(dayjs(endDateToCheck))) {
        const defaultBounds = getDefaultSchedulingBounds(localStartDate)
        // Convert UTC bounds to configured timezone for date picker
        adjustedStartDate = dayjs.utc(defaultBounds.start).tz(getTimezone()).toDate()
        adjustedEndDate = dayjs.utc(defaultBounds.end).tz(getTimezone()).toDate()
        setLocalStartDate(adjustedStartDate)
        setLocalEndDate(adjustedEndDate)
      }

      navigate({
        to: '/scheduling/$startDate/$endDate',
        params: {
          // Convert date picker dates (interpreted as configured timezone) to UTC for URL
          startDate: dayjs.tz(localStartDate, getTimezone()).utc().format('YYYY-MM-DDTHH:mm') + 'Z',
          endDate: dayjs.tz(adjustedEndDate, getTimezone()).utc().format('YYYY-MM-DDTHH:mm') + 'Z',
        },
      })
    }
  }

  const handleEndDateClose = () => {
    if (!localEndDate) return

    const decoded = decodeURIComponent(endDate)
    // Compare with UTC date converted to configured timezone
    const currentEndDate = dayjs.utc(decoded).tz(getTimezone()).toDate()
    const endDateChanged = !dayjs(localEndDate).isSame(currentEndDate, 'minute')

    if (endDateChanged) {
      const startDateToCheck = localStartDate || startDateObj
      let adjustedStartDate = startDateToCheck
      let adjustedEndDate = localEndDate

      // If end date is before start date, use default scheduling bounds
      if (dayjs(localEndDate).isBefore(dayjs(startDateToCheck))) {
        const defaultBounds = getDefaultSchedulingBounds(localEndDate)
        // Convert UTC bounds to configured timezone for date picker
        adjustedStartDate = dayjs.utc(defaultBounds.start).tz(getTimezone()).toDate()
        adjustedEndDate = dayjs.utc(defaultBounds.end).tz(getTimezone()).toDate()
        setLocalStartDate(adjustedStartDate)
        setLocalEndDate(adjustedEndDate)
      }

      navigate({
        to: '/scheduling/$startDate/$endDate',
        params: {
          // Convert date picker dates (interpreted as configured timezone) to UTC for URL
          startDate: dayjs.tz(adjustedStartDate, getTimezone()).utc().format('YYYY-MM-DDTHH:mm') + 'Z',
          endDate: dayjs.tz(adjustedEndDate, getTimezone()).utc().format('YYYY-MM-DDTHH:mm') + 'Z',
        },
      })
    }
  }

  const onSetStartDate = useDebouncedCallback((dateString: string | null) => {
    if (!dateString) {
      setLocalStartDate(null)
      return
    }

    const date = dayjs(dateString).toDate()
    setLocalStartDate(date)
  }, 300)

  const onSetEndDate = useDebouncedCallback((dateString: string | null) => {
    if (!dateString) {
      setLocalEndDate(null)
      return
    }

    const date = dayjs(dateString).toDate()
    setLocalEndDate(date)
  }, 300)

  const shiftDays = (offset: number) => {
    const tz = getTimezone()
    const currentStart = dayjs.utc(decodeURIComponent(startDate)).tz(tz)
    const currentEnd = dayjs.utc(decodeURIComponent(endDate)).tz(tz)
    const newStart = currentStart.add(offset, 'day')
    const newEnd = currentEnd.add(offset, 'day')
    navigate({
      to: '/scheduling/$startDate/$endDate',
      params: {
        startDate: newStart.utc().format('YYYY-MM-DDTHH:mm') + 'Z',
        endDate: newEnd.utc().format('YYYY-MM-DDTHH:mm') + 'Z',
      },
    })
  }

  const { data: vehicles } = useSuspenseQuery({
    ...getAllVehiclesOptions(),
  })

  const [channels, setChannels] = useLocalStorage<Channels>({
    key: CHANNELS_STORAGE_KEY,
    defaultValue: vehicles.map(v => ({
      key: v.id,
      label: v.id,
      visible: true,
      sortable: true,
      value: v.id,
    })) as Channels,
  })

  const visibleVehicles = useMemo(
    () => vehicles.filter(v => channels.some(c => c.key === v.id && c.visible)),
    [vehicles, channels]
  )

  const actions = (
    <Group>
      <Button
        leftSection={<TbMap2 size={16} color="light-dark(var(--mantine-color-gray-7), var(--mantine-color-gray-3))" />}
        onClick={openMap}
        variant="default"
        c="light-dark(var(--mantine-color-gray-7), var(--mantine-color-gray-3))"
      >
        {t('driveMap')}
      </Button>
      <ConfigureView columns={channels} onColumnsChange={setChannels} title={t('configureView', { ns: 'common' })} />
      <ActionIcon variant="default" onClick={() => shiftDays(-1)} aria-label={t('previousDay')}>
        <TbChevronLeft size={18} />
      </ActionIcon>
      <Tooltip label={t('startDate')} withArrow arrowOffset={10} position="top">
        <DateTimePicker
          key={`start-${startDate}`}
          placeholder={t('selectStartDate')}
          value={localStartDate}
          onChange={onSetStartDate}
          popoverProps={{
            onClose: handleStartDateClose,
          }}
        />
      </Tooltip>
      <Tooltip label={t('endDate')} withArrow arrowOffset={10} position="top">
        <DateTimePicker
          key={`end-${endDate}`}
          placeholder={t('selectEndDate')}
          value={localEndDate}
          onChange={onSetEndDate}
          popoverProps={{
            onClose: handleEndDateClose,
          }}
        />
      </Tooltip>
      <ActionIcon variant="default" onClick={() => shiftDays(1)} aria-label={t('nextDay')}>
        <TbChevronRight size={18} />
      </ActionIcon>
    </Group>
  )

  return (
    <>
      {!isErrorRoute && (
        <SchedulerPageShell
          title={t('scheduling')}
          action={actions}
          contentKey={visibleVehicles.map(v => v.id).join(',')}
          vehicles={visibleVehicles}
          startDate={decodeURIComponent(startDate)}
          endDate={decodeURIComponent(endDate)}
        >
          <Scheduler
            vehicles={visibleVehicles}
            intervalStart={decodeURIComponent(startDate)}
            intervalEnd={decodeURIComponent(endDate)}
          />
        </SchedulerPageShell>
      )}
      {mapOpened && (
        <DriveMapModal opened={mapOpened} onClose={closeMap} startDate={startDateObj} endDate={endDateObj} />
      )}
      <Outlet />
    </>
  )
}
